import React, { RefObject, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import type { ChipProps, SxProps, Theme } from '@mui/material';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { IconCheck, IconCloudDownload, IconPhoneOff } from '@tabler/icons-react';

import { formatDatePart, formatTimePart } from '@features/Admin/Campaign/utils/ongoing-calls.utils';
import { copyToClipboard } from '@pages/SuperAdmin/CompaniesPage/utils';
import { formatCallType, getSentimentsColor } from '../../utils';
import {
  IconChartColumn,
  IconCopyToBuffer,
  IconIndicatorFlag,
  IconMonitoring,
} from '@shared/assets/images/svgs';
import { ActionTableButton } from '@shared/components/actionTableButton/actionTableButton';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomTooltip } from '@shared/components/customTooltip';
import { CampaignCallStatus } from '@shared/constants/recipient-status.constants';
import { CallStatus } from '@shared/services/apiService/apiService';
import {
  RecipientAnalysis,
  RecipientData,
  SortableRecipientKeys,
} from '@pages/Admin/Campaign/ui/types';
import { CallAnalysisJsonTree } from './CallAnalysisJsonTree';
import { AuthenticatedPath } from '@shared/constants/routes.constants';

const TABLE_CHIP_SX: SxProps<Theme> = {
  height: '23px',
  bgcolor: 'primary.light',
};

const TableChip = ({ sx, ...props }: ChipProps) => {
  return <Chip {...props} sx={Array.isArray(sx) ? [TABLE_CHIP_SX, ...sx] : [TABLE_CHIP_SX, sx]} />;
};

const callIdColumnSx = {
  whiteSpace: 'nowrap',
  width: '1%',
  minWidth: 'max-content',
} as const;

const canPlayRecording = (row: RecipientData) =>
  row.recordingUrl != null && Boolean(row.answeredAt && row.callDuration);

const DateTimeCell = ({ timestamp }: { timestamp: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <Typography variant="subtitle1" fontWeight={500} lineHeight={1.3}>
        {formatDatePart(timestamp)}
      </Typography>
      <Typography
        variant="caption"
        lineHeight={1.3}
        sx={{ color: theme.palette.text.secondary, letterSpacing: '0.02em' }}>
        {formatTimePart(timestamp)}
      </Typography>
    </Box>
  );
};

interface OngoingCallsTableProps {
  calls: RecipientData[];
  onSort: (key: SortableRecipientKeys, fixedDirection?: 'asc' | 'desc') => void;
  onMonitorClick: (rowId: string, phone: string) => void;
  onInterruptClick: (callId: string) => void;
  interruptCallId: string | null;
  onInterruptDialogClose: () => void;
  onInterruptConfirm: () => void | Promise<void>;
  isInterruptingCall: boolean;
  tableRegionRef: RefObject<HTMLDivElement | null>;
  medianFooterRef: RefObject<HTMLDivElement | null>;
  loadMoreAnchorRef?: RefObject<HTMLDivElement | null>;
  tableMaxHeightPx: number | null;
  medianCallDurationLabel: string;
  isExtendedSearch: boolean;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  onRecordingClick: (callId: string) => void;
  classes: {
    tableheahder: string;
    table_first_cell: string;
    table_sort_cell: string;
    table_th: string;
    table_last_cell: string;
    table_row: string;
  };
}

export const OngoingCallsTable: React.FC<OngoingCallsTableProps> = ({
  calls,
  onSort,
  onMonitorClick,
  onInterruptClick,
  interruptCallId,
  onInterruptDialogClose,
  onInterruptConfirm,
  isInterruptingCall,
  tableRegionRef,
  medianFooterRef,
  loadMoreAnchorRef,
  tableMaxHeightPx,
  medianCallDurationLabel,
  isExtendedSearch,
  isLoading = false,
  isFetchingMore = false,
  onRecordingClick,
  classes,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId?: string }>();

  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<RecipientAnalysis | null>(null);
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getStatusColor = (status: CallStatus) => {
    if (status === CampaignCallStatus['IN-PROGRESS']) return theme.palette.primary.main;
    return theme.palette.extendedGrey?.neutral;
  };

  const canOpenAnalysis = (row: RecipientData) =>
    row.analysisStatus === 'completed' && row.analysis != null;

  const getAnalysisTooltip = (row: RecipientData) => {
    if (canOpenAnalysis(row)) return t('campaignPage.analysis');
    if (row.analysisStatus === 'queued') return t('campaignPage.analysisInQueue');
    if (row.analysisStatus === 'failed') return t('campaignPage.analysisFailed');
    return t('campaignPage.analysisEmpty');
  };

  const handleOpenAnalysis = (row: RecipientData) => {
    if (!canOpenAnalysis(row)) return;
    setSelectedAnalysis(row.analysis ?? null);
    setShowAnalysisDialog(true);
  };

  const handleCopyToClipboard = (rowId: string) => {
    copyToClipboard(rowId);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    setCopiedRowId(rowId);
    copiedTimerRef.current = setTimeout(() => setCopiedRowId(null), 2000);
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
      data: {
        title: t('campaignPage.copiedToClipboard.title'),
        description: t('campaignPage.copiedToClipboard.description'),
      },
    });
  };

  const handleCopyAnalysisJson = () => {
    copyToClipboard(JSON.stringify(selectedAnalysis, null, 2));
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
      data: {
        title: t('campaignPage.copiedToClipboard.title'),
        description: t('campaignPage.callAnalysis.viewer.copyJsonSuccess'),
      },
    });
  };

  const formatEndedReason = (reason: string | null) => {
    if (!reason) return 'Not Ended';
    if (reason.length < 16) return reason;
    return reason.slice(0, 16).concat('...');
  };

  const handleSortPhoneNumber = () => onSort('phoneNumber');
  const handleSortStartDate = () => onSort('startDate');
  const handleSortEndDate = () => onSort('endedAt');
  const handleSortCreatedDate = () => onSort('createdAt');
  const handleSortDuration = () => onSort('callDuration');
  const handleSortSentiments = (direction?: 'asc' | 'desc') => onSort('sentiments', direction);
  const canInterruptCall = (row: RecipientData) => row.status === CampaignCallStatus['IN-PROGRESS'];

  const navigateToCallDetail = (callId: string) => {
    if (!campaignId) return;
    navigate(
      generatePath(AuthenticatedPath.CALL_DETAIL, {
        campaignId,
        callId,
      }),
    );
  };

  const stopRowNavigation = (event: React.MouseEvent) => event.stopPropagation();

  if (isLoading && !calls.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!calls.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}>
        <Typography variant="body1" color="textSecondary">
          {t('campaignPage.emptyOngoingCalls')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box ref={tableRegionRef}>
        <TableContainer
          sx={{
            maxHeight: tableMaxHeightPx != null ? `${tableMaxHeightPx}px` : undefined,
            overflow: 'auto',
          }}>
          <Table
            stickyHeader
            sx={isExtendedSearch ? { width: 'max-content', minWidth: '100%' } : undefined}>
            <TableHead
              className={classes.tableheahder}
              sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
              <TableRow>
                <TableCell
                  onClick={handleSortPhoneNumber}
                  className={classes.table_first_cell}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.phoneNumber')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.status')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.endedReason')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  onClick={handleSortCreatedDate}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.createdDate')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  onClick={handleSortStartDate}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.startDate')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  onClick={handleSortEndDate}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.endDate')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  onClick={handleSortDuration}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.callDuration')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortSentiments()}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.sentimentIndi')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.agentRole')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.extendedGrey?.lighter,
                    ...(isExtendedSearch ? callIdColumnSx : {}),
                  }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.callId')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Box className={classes.table_sort_cell}>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.secondary}
                      className={classes.table_th}>
                      {t('campaignPage.ongoingCallsTable.action')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Typography
                    variant="h6"
                    color={theme.palette.text.secondary}
                    className={classes.table_th}
                    align="left">
                    {t('campaignPage.analysis')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={classes.table_last_cell}
                  sx={{ backgroundColor: theme.palette.extendedGrey?.lighter }}>
                  <Typography
                    variant="h6"
                    color={theme.palette.text.secondary}
                    className={classes.table_th}
                    align="left">
                    {t('campaignPage.ongoingCallsTable.recordingUrl')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((row) => (
                <TableRow
                  className={classes.table_row}
                  key={row.id}
                  hover
                  onClick={() => navigateToCallDetail(row.id)}>
                  <TableCell align="left" className={classes.table_first_cell}>
                    <Typography variant="subtitle1">
                      {row.phoneNumber || formatCallType(row.type)}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <CustomTooltip title={capitalize(row.status)}>
                      <span>
                        <TableChip
                          sx={{ color: getStatusColor(row.status) }}
                          label={capitalize(row.status)}
                        />
                      </span>
                    </CustomTooltip>
                  </TableCell>
                  <TableCell align="left">
                    <CustomTooltip title={row?.interactionResult}>
                      <span>
                        <TableChip
                          sx={{ color: theme.palette.extendedGrey?.neutral }}
                          label={formatEndedReason(row?.interactionResult)}
                        />
                      </span>
                    </CustomTooltip>
                  </TableCell>
                  <TableCell align="left">
                    {!row.createdAt ? (
                      <TableChip
                        sx={{ color: getStatusColor(row.status) }}
                        label={t('campaignPage.unavailable')}
                      />
                    ) : (
                      <DateTimeCell timestamp={row.createdAt} />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {!row.startDate ? (
                      <TableChip
                        sx={{ color: getStatusColor(row.status) }}
                        label={t('campaignPage.unavailable')}
                      />
                    ) : (
                      <DateTimeCell timestamp={row.startDate} />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {!row.endedAt ? (
                      <TableChip
                        sx={{ color: getStatusColor(row.status) }}
                        label={t('campaignPage.unavailable')}
                      />
                    ) : (
                      <DateTimeCell timestamp={row.endedAt} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing="10px">
                      {!row.callDuration && !row.startDate ? (
                        <TableChip
                          sx={{ color: getStatusColor(row.status) }}
                          label={t('campaignPage.unavailable')}
                        />
                      ) : (
                        <Typography variant="subtitle1">
                          {row.callDuration ? (
                            <>{row.callDuration} </>
                          ) : (
                            <TableChip
                              sx={{ color: getStatusColor(row.status) }}
                              label={t('campaignPage.unavailable')}
                            />
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {!row.sentiments.length ? (
                      <TableChip
                        sx={{ color: getStatusColor(row.status) }}
                        label={t('campaignPage.unavailable')}
                      />
                    ) : (
                      <Box
                        marginRight={10}
                        display={'flex'}
                        justifyContent="center"
                        sx={{ '& path': { stroke: getSentimentsColor(row.sentiments) } }}>
                        <IconIndicatorFlag />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing="10px">
                      {!row.agentRole ? (
                        <TableChip
                          sx={{ color: getStatusColor(row.status) }}
                          label={t('campaignPage.unavailable')}
                        />
                      ) : (
                        <Typography variant="subtitle1">
                          <>{row.agentRole} </>
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="left"
                    onClick={stopRowNavigation}
                    sx={{
                      paddingRight: 0,
                      ...(isExtendedSearch ? callIdColumnSx : {}),
                    }}>
                    {isExtendedSearch ? (
                      <Typography
                        variant="subtitle2"
                        onClick={() => handleCopyToClipboard(row.id)}
                        sx={{
                          cursor: 'pointer',
                          userSelect: 'text',
                          whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color:
                            copiedRowId === row.id
                              ? theme.palette.success.main
                              : theme.palette.text.primary,
                        }}>
                        {row.id}
                      </Typography>
                    ) : (
                      <Box display="flex" justifyContent="center" sx={{ m: 0 }}>
                        <ActionTableButton onClick={() => handleCopyToClipboard(row.id)}>
                          {copiedRowId === row.id ? (
                            <IconCheck size={16} color={theme.palette.success.main} />
                          ) : (
                            <IconCopyToBuffer />
                          )}
                        </ActionTableButton>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ paddingRight: 0 }} onClick={stopRowNavigation}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ActionTableButton
                        disabled={
                          !row.callDuration &&
                          !row.startDate &&
                          [
                            CampaignCallStatus.PENDING,
                            CampaignCallStatus.QUEUED,
                            CampaignCallStatus.RINGING,
                          ].includes(row.status)
                        }
                        onClick={() => onMonitorClick(row.mainId, row.phoneNumber)}>
                        <IconMonitoring />
                      </ActionTableButton>
                      {canInterruptCall(row) && (
                        <CustomTooltip title={t('campaignPage.ongoingCallsTable.interrupt.action')}>
                          <span>
                            <ActionTableButton
                              disabled={isInterruptingCall && interruptCallId === row.mainId}
                              onClick={() => onInterruptClick(row.mainId)}>
                              <IconPhoneOff size={16} color={theme.palette.error.main} />
                            </ActionTableButton>
                          </span>
                        </CustomTooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ paddingRight: 0 }} onClick={stopRowNavigation}>
                    <Box marginRight={10} display={'flex'} justifyContent="center">
                      <CustomTooltip title={getAnalysisTooltip(row)} placement="top" arrow>
                        <span>
                          <ActionTableButton
                            disabled={!canOpenAnalysis(row)}
                            onClick={() => handleOpenAnalysis(row)}>
                            <IconChartColumn />
                          </ActionTableButton>
                        </span>
                      </CustomTooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ paddingRight: 0 }} onClick={stopRowNavigation}>
                    <Box marginRight={10} display={'flex'} justifyContent="center">
                      <ActionTableButton
                        disabled={!canPlayRecording(row)}
                        onClick={() => onRecordingClick(row.mainId)}>
                        <Box sx={{ '& path': { stroke: '#7C8FAC' } }}>
                          <IconCloudDownload />
                        </Box>
                      </ActionTableButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {loadMoreAnchorRef && (
                <TableRow>
                  <TableCell colSpan={13} sx={{ border: 0, py: 1 }}>
                    <Box ref={loadMoreAnchorRef} sx={{ height: 1 }} />
                    {isFetchingMore && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box
        ref={medianFooterRef}
        gap={1}
        sx={{ marginTop: '30px', display: 'flex', flexDirection: 'row' }}>
        <Typography fontSize="14px">{t('campaignPage.medianCallDuration')}:</Typography>
        <Typography fontSize="14px" color={theme.palette.text.secondary}>
          {medianCallDurationLabel}
        </Typography>
      </Box>
      {showAnalysisDialog && (
        <Dialog
          open={showAnalysisDialog}
          onClose={() => setShowAnalysisDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 720,
            },
          }}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconChartColumn />
              {t('campaignPage.editCampaign.callAnalysis')}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1.5 }}>
            <CallAnalysisJsonTree value={selectedAnalysis} onCopyJson={handleCopyAnalysisJson} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAnalysisDialog(false)} variant="contained">
              {t('adminMainPage.operation.ok')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <ConfirmDialog
        showDialogFlag={interruptCallId != null}
        handleClose={onInterruptDialogClose}
        dialogTitle={'campaignPage.ongoingCallsTable.interrupt.title'}
        dialogContentText={t('campaignPage.ongoingCallsTable.interrupt.description')}
        showDialog={() => onInterruptDialogClose()}
        confirmDialog={onInterruptConfirm}
        variant="warning"
      />
    </>
  );
};
